import { Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React from "react";

import { activityApi } from "../../../../apis";
import {
  MetricValueAggregatedResponseDto,
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "../../../../libs/cellera-sdk";
import { MetricCard, MetricData, SectionWrapper } from "./index";

// Mapping from metric codes to display names and units - Overview only shows 2 items
const NUTRITION_HYDRATION_OVERVIEW_METRICS_MAP = {
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Calories]: {
    name: "Nutrition",
    unit: "kcal",
  },
  [MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration]: {
    name: "Hydration",
    unit: "fl oz",
  },
} as const;

type NutritionHydrationMetricCode =
  keyof typeof NUTRITION_HYDRATION_OVERVIEW_METRICS_MAP;

interface NutritionAndHydrationProps {
  patientId: string;
  onViewHistory: () => void;
}

// Individual metric calls - only for overview (Calories and Hydration)
const nutritionHydrationQueries = [
  {
    queryKey: "calories",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Calories,
  },
  {
    queryKey: "hydration",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration,
  },
];

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "No Data";

  // Handle numeric values
  if (typeof value === "number") {
    return value.toString();
  }

  return "No Data";
};

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No recent data available",
  suggestion: "Consider manual measurement",
  lastRecorded: "Not available",
});

const NutritionAndHydration: React.FC<NutritionAndHydrationProps> = ({
  patientId,
  onViewHistory,
}) => {
  const [
    { data: caloriesData, isLoading: caloriesLoading },
    { data: hydrationData, isLoading: hydrationLoading },
  ] = useQueries({
    queries: nutritionHydrationQueries.map((query) => ({
      queryKey: ["nutritionHydration", patientId, query.queryKey],
      queryFn: () => {
        if (!patientId) return null;

        const request: MetricValuesAggregationRequestDto = {
          metricCode: query.metricCode,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [
            MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
          ],
          orderBy: "DESC",
        };

        return activityApi.getMetricValuesAggregatedByPatientId({
          patientId,
          metricValuesAggregationRequestDto: request,
        });
      },
      enabled: !!patientId,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Map individual data to MetricData format - only for overview
  const metricDataMap: Record<
    string,
    MetricValueAggregatedResponseDto | undefined
  > = {
    [MetricValuesAggregationRequestDtoMetricCodeEnum.Calories]:
      caloriesData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration]:
      hydrationData?.data[0],
  };

  const displayData: MetricData[] = Object.keys(
    NUTRITION_HYDRATION_OVERVIEW_METRICS_MAP
  ).map((metricCode) => {
    const metricInfo =
      NUTRITION_HYDRATION_OVERVIEW_METRICS_MAP[
        metricCode as NutritionHydrationMetricCode
      ];
    const latestValue = metricDataMap[metricCode];

    if (!latestValue) {
      return {
        id: metricCode,
        name: metricInfo.name,
        value: "No Data",
        unit: metricInfo.unit,
        lastChecked: "Never",
        hasData: false,
        fallbackInfo: getFallbackInfo(),
      };
    }

    const formattedValue = formatValue(
      latestValue.valueJsonData?.latest ??
        latestValue.valueIntData?.latest ??
        latestValue.valueFloatData?.latest
    );

    const isNoData = formattedValue === "No Data";

    return {
      id: metricCode,
      name: metricInfo.name,
      value: formattedValue,
      unit: metricInfo.unit,
      lastChecked: new Date(latestValue.startDate).toLocaleString("vi-VN"),
      hasData: !isNoData,
      fallbackInfo: isNoData ? getFallbackInfo() : undefined,
    };
  });

  const isLoading = caloriesLoading || hydrationLoading;

  if (isLoading) {
    return (
      <SectionWrapper title="Nutrition and Hydration" onViewAll={onViewHistory}>
        <Typography>Loading nutrition and hydration data...</Typography>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Nutrition and Hydration"
      onViewAll={onViewHistory}
      spacing="compact"
    >
      {displayData.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </SectionWrapper>
  );
};

export default NutritionAndHydration;
